import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@wondermaps/ui";

interface ExpandableTextBaseProps {
  lines: number;
  className?: string;
  textClassName?: string;
  onExpand?: () => void;
  /** Controlled expanded state. When provided, parent owns the toggle (used for "click anywhere to expand" patterns). */
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  /** Toggle button color override (e.g. brand accent). Falls back to text-primary. */
  accentColor?: string;
  /** Notify parent whether content overflows (so parent can gate "click anywhere to expand"). */
  onCanExpandChange?: (canExpand: boolean) => void;
}

type ExpandableTextProps =
  | (ExpandableTextBaseProps & { text: string; html?: never })
  | (ExpandableTextBaseProps & { html: string; text?: never });

/**
 * Visitor read-more component. Uses CSS line-clamp for truncation and DOM
 * measurement (ResizeObserver) to decide whether to render the toggle —
 * char-length proxies are unreliable for HTML and variable-width fonts.
 */
export function ExpandableText({
  lines,
  className,
  textClassName,
  onExpand,
  expanded: expandedProp,
  onExpandedChange,
  accentColor,
  onCanExpandChange,
  ...rest
}: ExpandableTextProps) {
  const { t } = useTranslation("common");
  const contentRef = useRef<HTMLDivElement>(null);
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isControlled = expandedProp !== undefined;
  const expanded = isControlled ? expandedProp! : internalExpanded;
  const setExpanded = (next: boolean) => {
    if (!isControlled) setInternalExpanded(next);
    onExpandedChange?.(next);
    if (next) onExpand?.();
  };
  // Latches true once content has been measured as overflowing in the
  // collapsed state. Stays true while expanded so the "Show less" toggle
  // remains visible (scrollHeight==clientHeight when unclamped).
  const [canExpand, setCanExpand] = useState(false);

  const measure = () => {
    const el = contentRef.current;
    if (!el) return;
    if (expanded) return; // only measure in clamped state
    const overflowing = el.scrollHeight > el.clientHeight + 1;
    if (overflowing && !canExpand) {
      setCanExpand(true);
      onCanExpandChange?.(true);
    } else if (!overflowing && canExpand) {
      // content shrank below clamp threshold (e.g. resize) — release latch
      setCanExpand(false);
      onCanExpandChange?.(false);
      if (expanded) setExpanded(false);
    }
  };

  useLayoutEffect(() => {
    measure();
  });

  useEffect(() => {
    const el = contentRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const clampStyle = expanded
    ? undefined
    : ({
        display: "-webkit-box",
        WebkitBoxOrient: "vertical" as const,
        WebkitLineClamp: lines,
        overflow: "hidden",
      } as const);

  return (
    <div className={className}>
      {"html" in rest && rest.html !== undefined ? (
        <div
          ref={contentRef}
          className={textClassName}
          style={clampStyle}
          dangerouslySetInnerHTML={{ __html: rest.html }}
        />
      ) : (
        <div ref={contentRef} className={textClassName} style={clampStyle}>
          {rest.text}
        </div>
      )}
      {canExpand && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
          className={cn(
            "mt-1 text-xs font-medium hover:underline",
            !accentColor && "text-primary",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded",
          )}
          style={accentColor ? { color: accentColor } : undefined}
        >
          {expanded ? t("showLess") : t("readMore")}
        </button>
      )}
    </div>
  );
}
